import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Bell, Check, Loader2, X } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 60_000;

const TYPE_LABEL = {
  NEW_BOOKING: 'New booking',
  BOOKING_CONFIRMED: 'Confirmed',
  BOOKING_CANCELED: 'Canceled',
  BOOKING_RESCHEDULED: 'Rescheduled',
  CONSULTATION_BOOKED: 'Consultation',
  CLIENT_ALERT: 'Client alert',
  PROMO_EXPIRING: 'Promo',
};

const TYPE_DOT = {
  NEW_BOOKING: 'bg-blue-500',
  BOOKING_CONFIRMED: 'bg-green-500',
  BOOKING_CANCELED: 'bg-red-500',
  BOOKING_RESCHEDULED: 'bg-amber-500',
  CONSULTATION_BOOKED: 'bg-indigo-500',
  CLIENT_ALERT: 'bg-rose-500',
  PROMO_EXPIRING: 'bg-orange-500',
};

/**
 * In-app notification bell. Polls /api/notifications/unread-count every 60s.
 * Click to open a dropdown of the 50 most recent notifications.
 */
export default function NotificationBell() {
  const { token } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  const loadUnreadCount = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API_URL}/api/notifications/unread-count`, { headers });
      setUnread(res.data?.count || 0);
    } catch (_) {
      // silent — bell is non-critical
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadItems = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/notifications?limit=50`, { headers });
      setItems(res.data || []);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Initial fetch + poll
  useEffect(() => {
    loadUnreadCount();
    const id = setInterval(loadUnreadCount, POLL_MS);
    return () => clearInterval(id);
  }, [loadUnreadCount]);

  // When dropdown opens, fetch full list
  useEffect(() => {
    if (open) loadItems();
  }, [open, loadItems]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const markRead = async (id) => {
    try {
      await axios.patch(`${API_URL}/api/notifications/${id}/read`, {}, { headers });
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
      setUnread((u) => Math.max(0, u - 1));
    } catch (_) {
      toast.error('Could not mark as read');
    }
  };

  const removeOne = async (id, evt) => {
    evt?.stopPropagation();
    try {
      await axios.delete(`${API_URL}/api/notifications/${id}`, { headers });
      setItems((prev) => prev.filter((n) => n.id !== id));
      loadUnreadCount();
    } catch (_) {
      toast.error('Could not delete');
    }
  };

  const markAll = async () => {
    try {
      await axios.post(`${API_URL}/api/notifications/read-all`, {}, { headers });
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnread(0);
      toast.success('All caught up');
    } catch (_) {
      toast.error('Could not mark all');
    }
  };

  return (
    <div className="relative">
      <Button
        ref={buttonRef}
        variant="ghost"
        size="icon"
        onClick={() => setOpen((o) => !o)}
        className="relative"
        aria-label="Notifications"
        data-testid="notification-bell"
      >
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <Badge
            className="absolute -top-1 -right-1 h-5 min-w-[20px] px-1 rounded-full bg-red-500 hover:bg-red-500 text-white text-[10px] flex items-center justify-center"
            data-testid="notification-badge"
          >
            {unread > 9 ? '9+' : unread}
          </Badge>
        )}
      </Button>

      {open && (
        <div
          ref={dropdownRef}
          className="absolute right-0 mt-2 w-80 sm:w-96 bg-card border rounded-lg shadow-lg z-50 max-h-[70vh] overflow-hidden flex flex-col"
          data-testid="notification-dropdown"
        >
          <div className="px-4 py-3 border-b flex items-center justify-between bg-muted/30">
            <p className="font-semibold text-sm">Notifications</p>
            {items.some((n) => !n.read) && (
              <button
                onClick={markAll}
                className="text-xs text-primary hover:underline"
                data-testid="mark-all-read"
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="overflow-y-auto flex-1">
            {loading ? (
              <div className="flex justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : items.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                You're all caught up.
              </p>
            ) : (
              <ul className="divide-y">
                {items.map((n) => (
                  <li
                    key={n.id}
                    onClick={() => !n.read && markRead(n.id)}
                    className={`px-4 py-3 cursor-pointer hover:bg-muted transition-colors flex gap-3 ${
                      !n.read ? 'bg-blue-50/40' : ''
                    }`}
                    data-testid={`notification-${n.id}`}
                  >
                    <span
                      className={`mt-1.5 h-2 w-2 rounded-full flex-shrink-0 ${
                        TYPE_DOT[n.type] || 'bg-gray-400'
                      }`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-2">
                        <p className="text-sm font-semibold flex-1">{n.title}</p>
                        {!n.read && (
                          <span className="h-2 w-2 rounded-full bg-blue-500 flex-shrink-0 mt-1" />
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                        {n.message}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] uppercase tracking-wide font-medium text-muted-foreground">
                          {TYPE_LABEL[n.type] || n.type}
                        </span>
                        <span className="text-[10px] text-muted-foreground">·</span>
                        <span className="text-[10px] text-muted-foreground">
                          {n.createdAt &&
                            formatDistanceToNow(parseISO(n.createdAt), { addSuffix: true })}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      {!n.read && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            markRead(n.id);
                          }}
                          className="text-muted-foreground hover:text-foreground"
                          title="Mark as read"
                        >
                          <Check className="h-3.5 w-3.5" />
                        </button>
                      )}
                      <button
                        onClick={(e) => removeOne(n.id, e)}
                        className="text-muted-foreground hover:text-red-600"
                        title="Delete"
                        data-testid={`notification-delete-${n.id}`}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
